/*
 * Settings → master data and configuration (Tenant/Settings/*), as the real
 * API answers it, built from the prototype's sample lists in mock/masters.js
 * and mock/settings.js. Writes change the sample in memory and go into the
 * sample change log (configLog), so the change history behaves as it will.
 *
 * A few figures the real API cannot give yet — customers per location,
 * subscriptions per package, tickets per priority — ride along as extra
 * fields (`customers_count`, `subscriptions_count`…) so the prototype keeps
 * showing them; the screens fall back to "—" without them.
 */
import { ok, created, fail, invalid, notFound, paginate, matches, nextId, now } from "./util";
import {
  departments, teams, staff as staffSample, locations as locationSample, segments as segmentSample, industries, leadSources, taskTypes,
  priorities as prioritySample, communicationTypes, documentTypes, competitors as competitorSample, products as productSample,
  packages as packageSample, prospectStages, leadStages, pipelineStages, prospectQualification, leadQualification,
  reasonCategories, reasonCodes, slaPolicies,
} from "@/mock/masters";
import {
  configLog, recordChange, usageOf, departmentStatus, territoryRouting, salesRegions, packageDetails, priceLists, onboardingStages,
  ticketStatuses, transitions as transitionSample, requiredOnEntry, conversionRules, pipelineRules, duplicateFieldOptions, reasonExtras,
  complaintCategories, ticketCategories, churnTriggers, customFields as customFieldSample, businessHours, holidays as holidaySample,
  slaOverrides, escalationLevels, numberingFormats, plan,
} from "@/mock/settings";
import { users } from "@/mock/admin";
import { customers } from "@/mock/customers";
import { prospects } from "@/mock/prospects";
import { parseDuration } from "@/helpers/durations";
import { daysFromNow, daysUntil } from "@/helpers/crm";

const BASE = "tenant/settings";
const today = () => daysFromNow(0);
const same = (a, b) => String(a) === String(b);
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

/* ------------------------------------------------------------------ */
/* The vocabulary (settings/meta)                                       */
/* ------------------------------------------------------------------ */

const roles = [
  ["sales", "Sales"], ["sales-manager", "Sales Manager"], ["customer-success", "Customer Success"], ["implementation", "Implementation"],
  ["support", "Support"], ["marketing", "Marketing"], ["finance", "Finance"], ["management", "Management"], ["admin", "CTO/Admin"], ["audit-risk", "Audit/Risk"],
].map(([key, label]) => ({ key, label }));
const roleKey = (label) => roles.find((r) => r.label === label)?.key ?? label;

const pairs = (o) => Object.entries(o).map(([key, label]) => ({ key, label }));
const meta = {
  areas: pairs({ organisation: "Departments, teams & staff", territories: "Locations & territories", products: "Products & packages", stages: "Stages & conversion rules", "reason-codes": "Reason codes", lookups: "Lookup lists", "custom-fields": "Custom fields", sla: "SLA policies", numbering: "Numbering" }),
  product_statuses: [{ key: "active", name: "Active", tone: "success" }, { key: "in_development", name: "In development", tone: "info" }, { key: "retired", name: "Retired", tone: "secondary" }],
  deployments: pairs({ platform: "Multi-tenant platform", standalone: "Standalone application" }),
  billing_periods: ["Monthly", "Monthly per tenant", "Monthly per KES 100M loan book", "Monthly per installation", "Quarterly", "Annual", "One-off", "Not on sale yet"],
  routing_rules: pairs({ round_robin: "Round robin", least_open: "Least open records", manual: "Manual (manager assigns)", pool: "Claim from territory pool" }),
  stage_kinds: pairs({ open: "Open", won: "Won / done", lost: "Lost / failed", paused: "Paused" }),
  reason_points: pairs({ prospect: "Prospect", lead: "Lead", opportunity: "Opportunity", onboarding: "Onboarding", churn: "Churn", exit: "Exit" }),
  tones: ["primary", "secondary", "success", "info", "warning", "danger", "dark", "light", "purple"],
  custom_field_entities: pairs({ prospect: "Prospect", lead: "Lead", opportunity: "Opportunity", customer: "Customer", contact: "Contact", ticket: "Ticket", onboarding: "Onboarding" }),
  custom_field_types: pairs({ text: "Text", number: "Number", date: "Date", dropdown: "Dropdown", multiselect: "Multi-select", boolean: "Yes / no", currency: "Currency" }),
  custom_field_limit: plan.limits.find((l) => l.key === "custom_fields")?.limit ?? 50,
  channels: pairs({ in_app: "In-app", email: "Email", sms: "SMS" }),
  roles,
};

/* ------------------------------------------------------------------ */
/* Organisation                                                         */
/* ------------------------------------------------------------------ */

const staffRows = staffSample.map((s) => ({
  id: s.id,
  staff_no: s.staff_no,
  name: s.name,
  title: s.role,
  email: null,
  phone: null,
  department_id: s.team_id ? teams.find((t) => t.id === s.team_id)?.department_id : s.role === "Finance" ? 6 : null,
  team_id: s.team_id,
  supervisor_id: staffSample.find((x) => x.name === s.supervisor)?.id ?? null,
  user_id: users.find((u) => u.staff_id === s.id)?.id ?? null,
  status: s.status,
  owns: usageOf(`staff-${s.staff_no}`),
}));
const deptRows = departments.map((d) => ({
  id: d.id, code: d.code, name: d.name, description: null, head_staff_id: staffSample.find((s) => s.name === d.head)?.id ?? null,
  is_active: departmentStatus[d.id] !== "inactive",
}));
const teamRows = teams.map((t) => ({ id: t.id, name: t.name, department_id: t.department_id, lead_staff_id: staffSample.find((s) => s.name === t.lead)?.id ?? null, is_active: true }));

const person = (id) => {
  const s = staffRows.find((x) => same(x.id, id));
  return s ? { id: s.id, name: s.name } : null;
};
const deptRef = (id) => {
  const d = deptRows.find((x) => same(x.id, id));
  return d ? { id: d.id, name: d.name } : null;
};
const teamRef = (id) => {
  const t = teamRows.find((x) => same(x.id, id));
  return t ? { id: t.id, name: t.name } : null;
};

/* ------------------------------------------------------------------ */
/* Territories                                                          */
/* ------------------------------------------------------------------ */

const regionRows = salesRegions.map((name, i) => ({ id: i + 1, name, is_active: true }));
const territoryRows = [...new Set(locationSample.map((l) => l.territory))].map((name, i) => ({
  id: i + 1, name, is_active: true,
  sales_region_id: regionRows.find((r) => r.name === locationSample.find((l) => l.territory === name).region)?.id,
}));
const locationRows = locationSample.map((l) => ({
  id: l.id, country: l.country, county: l.county, town: l.town, is_active: true,
  territory_id: territoryRows.find((t) => t.name === l.territory).id,
}));
const ruleKey = (label) => meta.routing_rules.find((r) => r.label === label)?.key ?? "manual";
const routeRows = territoryRouting.map((r, i) => ({
  id: i + 1,
  territory_id: territoryRows.find((t) => t.name === r.territory).id,
  team_id: r.team_id,
  backup_team_id: r.backup_team_id,
  rule: ruleKey(r.rule),
  segments: r.segments.map((c) => c.toLowerCase()),
  sort_order: i + 1,
  is_active: true,
}));

/* ------------------------------------------------------------------ */
/* Lookups (segments and priorities are needed by routing and SLA)      */
/* ------------------------------------------------------------------ */

let lookupId = 1000;
const L = (list, prefix, items, map = (v) => ({ value: slug(v), label: v, meta: {} })) =>
  items.map((v, i) => {
    const row = map(v);
    return { id: lookupId++, list, sort_order: i + 1, is_active: true, is_default: true, product: null, in_use: usageOf(`${prefix}:${row.label}`), ...row };
  });

const lookupLists = {
  industries: { name: "Industries", singular: "industry", help: "Industry of prospects and customers.", used_in: "Prospects, leads, customers, reports by industry", fields: [], rows: L("industries", "ind", industries) },
  segments: {
    name: "Customer segments", singular: "segment", help: "How customers are grouped by size or importance.", used_in: "Customers, SLA overrides, routing, reports",
    fields: [{ key: "code", label: "Code" }, { key: "rule", label: "Rule" }],
    rows: L("segments", "seg", segmentSample, (s) => ({ value: s.code.toLowerCase(), label: s.name, meta: { code: s.code, rule: s.rule } })),
  },
  lead_sources: { name: "Lead sources", singular: "lead source", help: "Where prospects and leads come from.", used_in: "Prospects, leads, lead source performance report", fields: [], rows: L("lead_sources", "src", leadSources) },
  task_types: { name: "Task types", singular: "task type", help: "Kinds of follow-up and next action.", used_in: "Next actions, follow-ups, activity reports", fields: [], rows: L("task_types", "task", taskTypes) },
  priorities: {
    name: "Priorities", singular: "priority", help: "Priority of tickets, tasks and complaints. SLA targets hang off these.", used_in: "Tickets, complaints, tasks, SLA",
    fields: [{ key: "tone", label: "Colour", options: ["secondary", "info", "warning", "danger", "primary", "success"] }],
    rows: L("priorities", "pri", prioritySample, (p) => ({ value: p.key, label: p.name, meta: { tone: p.tone } })),
  },
  communication_types: { name: "Communication types", singular: "communication type", help: "How a conversation happened.", used_in: "Interactions on every timeline, engagement score", fields: [], rows: L("communication_types", "com", communicationTypes) },
  document_types: { name: "Document types", singular: "document type", help: "Classifies uploaded files.", used_in: "Documents on every record, onboarding templates", fields: [], rows: L("document_types", "doc", documentTypes, (v) => ({ value: slug(v).toUpperCase(), label: v, meta: {} })) },
  competitors: {
    name: "Competitors", singular: "competitor", help: "Who you lose to. Required with 'lost to competitor' reasons.", used_in: "Lost opportunities, exits, win/loss analysis",
    fields: [{ key: "product", label: "Product" }, { key: "category", label: "Category" }],
    rows: L("competitors", "cmp", competitorSample, (c) => ({ value: slug(c.name), label: c.name, meta: { product: c.product, category: c.category } })),
  },
  complaint_categories: { name: "Complaint categories", singular: "complaint category", help: "Classifies customer complaints for root-cause reporting.", used_in: "Complaints, complaints report", fields: [], rows: L("complaint_categories", "cpl", complaintCategories) },
  ticket_categories: { name: "Ticket categories", singular: "ticket category", per_product: true, help: "Classifies support tickets; shared ones plus each product's own (Payroll, Leave… for HRM; Disbursement, Repayments… for LMS). Served to products by GET /api/v1/support/meta.", used_in: "Tickets, client connections (Support API), support analytics", fields: [], rows: L("ticket_categories", "tkt", ticketCategories) },
  churn_triggers: { name: "Churn triggers", singular: "churn trigger", help: "Why a customer was flagged at risk.", used_in: "Churn risk flags, retention actions, churn analysis", fields: [], rows: L("churn_triggers", "chn", churnTriggers) },
};
// Values the company has already changed, so the change history matches what is shown.
lookupLists.lead_sources.rows.push({ id: lookupId++, list: "lead_sources", value: "tiktok", label: "TikTok", sort_order: 99, is_active: true, is_default: false, meta: {}, product: null, in_use: 3 });
lookupLists.industries.rows.push({ id: lookupId++, list: "industries", value: "telecoms", label: "Telecoms", sort_order: 99, is_active: false, is_default: false, meta: {}, product: null, in_use: 6 });

const segmentByValue = (v) => lookupLists.segments.rows.find((r) => r.value === v);
const priorityRow = (idOrValue) => lookupLists.priorities.rows.find((r) => same(r.id, idOrValue) || r.value === idOrValue);

/* ------------------------------------------------------------------ */
/* Products                                                             */
/* ------------------------------------------------------------------ */

const productRows = productSample.map((p) => ({
  id: p.id, code: p.code, name: p.name, short_name: p.short, owner_staff_id: staffSample.find((s) => s.name === p.owner)?.id ?? null,
  status: p.status, deployment: p.deployment, is_sellable: p.sellable !== false, description: p.note ?? null, domain: p.domain ?? null,
  tenant_prefix: p.tenant_prefix ?? null, expected_release: p.expected ?? null,
}));
const packageRows = packageSample.map((k) => ({
  id: k.id, product_id: k.product_id, name: k.name, price: k.price, currency: "KES", billing_period: packageDetails[k.id]?.billing ?? "Monthly",
  unit_label: k.unit, included_users: k.users, features: [...(packageDetails[k.id]?.features ?? [])], is_sellable: k.sellable, is_active: true,
  effective_from: null, effective_to: null, subscriptions_count: usageOf(`pkg-${k.id}`) % 25,
}));
const userRef = (name) => {
  const u = users.find((x) => x.name === name);
  return u ? { id: u.id, name: u.name } : { id: null, name };
};
const priceRows = [
  { id: 1, package_id: 5, previous_price: 180000, price: 195000, effective_from: daysFromNow(30), effective_to: null, status: "pending", reason: "Multi-branch demand; aligns with competitor pricing", requested_by: userRef("Brian Otieno"), decided_by: null, decided_at: null, decision_note: null, created_at: now() },
];
const priceListRows = priceLists.map((l) => ({
  id: l.id, name: l.name, effective_from: l.effective_from, effective_to: l.effective_to, note: l.note ?? null,
  status: l.status === "scheduled" ? "pending" : "approved",
  requested_by: userRef(l.status === "scheduled" ? "Brian Otieno" : "Mercy Wambui"), decided_by: l.status === "scheduled" ? null : userRef("Mercy Wambui"),
  decided_at: null, is_active: true,
}));
const periodOf = (l) => (daysUntil(l.effective_from) > 0 ? "scheduled" : l.effective_to && daysUntil(l.effective_to) < 0 ? "expired" : "current");

/* ------------------------------------------------------------------ */
/* Stages, transitions, qualification, rules                            */
/* ------------------------------------------------------------------ */

const stageEntities = {
  prospect: { label: "Prospect stages", singular: "Prospect stage", needs_lost: true, probability: false, source: prospectStages },
  lead: { label: "Lead stages", singular: "Lead stage", needs_lost: true, probability: false, source: leadStages },
  opportunity: { label: "Opportunity stages", singular: "Opportunity stage", needs_lost: true, probability: true, source: pipelineStages },
  onboarding: { label: "Onboarding stages", singular: "Onboarding stage", needs_lost: true, probability: false, source: onboardingStages },
  ticket: { label: "Ticket statuses", singular: "Ticket status", needs_lost: false, probability: false, source: ticketStatuses },
};
let stageId = 1;
const stageLists = Object.fromEntries(
  Object.entries(stageEntities).map(([entity, e]) => [
    entity,
    {
      stages: e.source.map((s) => ({
        id: stageId++, entity, key: s.key, name: s.name, default_name: s.name, kind: s.kind, sort_order: s.order,
        probability: e.probability ? s.probability ?? null : null, max_days: s.max_days ?? null,
        required_on_entry: [...(requiredOnEntry[entity]?.[s.key] ?? [])], is_system: true, is_active: true, in_use: usageOf(`${entity}.${s.key}`),
      })),
      transitions: Object.entries(transitionSample[entity] ?? {}).flatMap(([from, tos]) => tos.map((to) => ({ id: `${from}>${to}`, from, to, required_fields: [] }))),
    },
  ]),
);
const defaultStages = structuredClone(stageLists);

let qualId = 1;
const qualificationRows = [
  ...prospectQualification.map((q, i) => ({ id: qualId++, entity: "prospect", key: q.key, label: q.label, is_required: q.required, sort_order: i + 1, is_active: true, in_use: usageOf(q.key) % 50 })),
  ...leadQualification.map((q, i) => ({ id: qualId++, entity: "lead", key: q.key, label: q.label, is_required: q.required, sort_order: i + 1, is_active: true, in_use: 0, missing_count: usageOf(`lq${q.key}`) % 40 })),
];

const workflowRules = () => ({
  conversion_rules: conversionRules.map((r) => ({ ...r })),
  pipeline: structuredClone(pipelineRules),
  duplicate_field_options: duplicateFieldOptions,
});

/* ------------------------------------------------------------------ */
/* Reasons                                                              */
/* ------------------------------------------------------------------ */

const categoryRows = reasonCategories.map((c, i) => ({ id: i + 1, key: c.key, name: c.name, tone: c.tone, sort_order: i + 1, is_active: true }));
const reasonRows = [
  ...reasonCodes.map((r, i) => ({
    id: i + 1, code: r.code, name: r.name, reason_category_id: categoryRows.find((c) => c.key === r.category).id, applies_to: [...r.applies_to],
    needs_competitor: r.needs_competitor, recyclable: r.recyclable, default_revisit_days: reasonExtras[r.code]?.revisit_days ?? null,
    preventable: r.preventable, note_required: reasonExtras[r.code]?.note_required ?? false, sort_order: i + 1, is_active: true, in_use: usageOf(r.code),
  })),
  // A retired reason kept for the records that still carry it.
  { id: 100, code: "CMP-03", name: "Chose a competitor on relationship", reason_category_id: categoryRows.find((c) => c.key === "competitor").id, applies_to: ["opportunity"], needs_competitor: true, recyclable: true, default_revisit_days: 300, preventable: true, note_required: false, sort_order: 100, is_active: false, in_use: 4 },
];

/* ------------------------------------------------------------------ */
/* Custom fields                                                        */
/* ------------------------------------------------------------------ */

const typeKey = (t) => ({ "multi-select": "multiselect", "yes/no": "boolean" })[t] ?? t;
const fieldRows = customFieldSample.map((f, i) => ({
  id: f.id, entity: f.entity.toLowerCase(), key: f.key, label: f.label, type: typeKey(f.type), options: [...f.options], is_required: f.required,
  show_on_create: f.on_create, visible_to_roles: f.roles.includes("All") ? ["*"] : f.roles.map(roleKey), is_sensitive: f.sensitive, sort_order: i + 1,
  is_active: f.active, in_use: f.usage,
}));

/* ------------------------------------------------------------------ */
/* SLA                                                                  */
/* ------------------------------------------------------------------ */

const day = (d, i) => ({ weekday: i + 1, day: d.day, is_working: d.working, opens_at: d.open || null, closes_at: d.close || null });
const allWeek = (open, close) => businessHours.map((d, i) => ({ weekday: i + 1, day: d.day, is_working: true, opens_at: open, closes_at: close }));
const calendarRows = [
  { id: 1, name: "Business hours", timezone: "Africa/Nairobi", observes_holidays: true, is_default: true, is_active: true, days: businessHours.map(day) },
  { id: 2, name: "24 × 7", timezone: "Africa/Nairobi", observes_holidays: false, is_default: false, is_active: true, days: allWeek("00:00", "23:59") },
  { id: 3, name: "Extended (07:00–22:00)", timezone: "Africa/Nairobi", observes_holidays: true, is_default: false, is_active: true, days: allWeek("07:00", "22:00") },
];
const calendarByName = (name) => calendarRows.find((c) => c.name === name) ?? calendarRows[0];
const holidayRows = holidaySample.map((h, i) => ({ id: i + 1, date: h.date, name: h.name }));

const policyRows = [
  ...slaPolicies.map((p, i) => ({
    id: i + 1, name: null, priority_id: priorityRow(p.priority).id, segment_id: null, package_id: null, applies_to: "All customers",
    response_minutes: parseDuration(p.response), resolution_minutes: parseDuration(p.resolution), business_calendar_id: calendarByName(p.priority === "critical" ? "24 × 7" : "Business hours").id,
    escalate_after_minutes: parseDuration(p.escalate_after), escalate_to_type: "role", escalate_to_value: "support", escalate_to_label: p.escalate_to,
    is_active: true, tickets_count: usageOf(`sla-${p.priority}`),
  })),
  ...slaOverrides.map((o) => {
    const [kind, target] = o.applies_to.split(": ");
    const seg = kind === "Segment" ? lookupLists.segments.rows.find((s) => s.label === target) : null;
    return {
      id: 100 + o.id, name: null, priority_id: priorityRow(o.priority).id, segment_id: seg?.id ?? null,
      package_id: kind === "Package" ? 3 : null, applies_to: o.applies_to,
      response_minutes: parseDuration(o.response), resolution_minutes: parseDuration(o.resolution), business_calendar_id: calendarByName(o.calendar === "24 × 7" ? "24 × 7" : "Business hours").id,
      escalate_after_minutes: null, escalate_to_type: null, escalate_to_value: null, escalate_to_label: null,
      is_active: o.active, covered_count: usageOf(`ovr-${o.id}`), is_override: true,
    };
  }),
];
const levelRows = escalationLevels.map((l, i) => ({
  id: i + 1, level: l.level, basis: /resolution/i.test(l.when) ? "resolution" : "response",
  after_percent: Number((l.when.match(/(\d+)%/) ?? [0, 100])[1]), after_minutes: 0, priority_id: /Critical/.test(l.when) ? priorityRow("critical").id : null,
  when: l.when, escalate_to_type: "role", escalate_to_value: "support", escalate_to_label: l.who,
  channels: l.channel.split(",").map((c) => ({ "In-app": "in_app", Email: "email", SMS: "sms" })[c.trim()] ?? "in_app"), channels_label: l.channel, is_active: true,
}));

/* ------------------------------------------------------------------ */
/* Numbering                                                            */
/* ------------------------------------------------------------------ */

const DEFAULT_FORMAT = "{prefix}-{year}-{seq}";
const formatRows = numberingFormats.map((f) => ({
  key: f.key, label: f.record, prefix: f.prefix, format: f.format, padding: f.padding, reset_yearly: f.reset_yearly, next_number: f.next,
  default: { prefix: f.prefix, format: DEFAULT_FORMAT, padding: f.padding, reset_yearly: true },
}));
const renderNumber = (f, n = f.next_number) => {
  const d = new Date();
  return f.format.replaceAll("{prefix}", f.prefix).replaceAll("{year}", String(d.getFullYear())).replaceAll("{month}", String(d.getMonth() + 1).padStart(2, "0")).replaceAll("{seq}", String(n).padStart(f.padding || 1, "0"));
};
const presentFormat = (f) => ({ ...f, preview: renderNumber(f), customised: f.format !== DEFAULT_FORMAT, period: f.reset_yearly ? String(new Date().getFullYear()) : null });

/* ------------------------------------------------------------------ */
/* Presenters: the resources' shapes                                    */
/* ------------------------------------------------------------------ */

const presentDepartment = (d) => ({
  ...d,
  head: person(d.head_staff_id),
  teams_count: teamRows.filter((t) => same(t.department_id, d.id)).length,
  staff_count: staffRows.filter((s) => same(s.department_id, d.id)).length,
  in_use: teamRows.filter((t) => same(t.department_id, d.id)).length + staffRows.filter((s) => same(s.department_id, d.id)).length,
});
const presentTeam = (t) => {
  const members = staffRows.filter((s) => same(s.team_id, t.id));
  return {
    ...t,
    department: deptRef(t.department_id),
    lead: person(t.lead_staff_id),
    members: members.map((m) => ({ id: m.id, name: m.name, status: m.status })),
    territories: [...new Set(routeRows.filter((r) => r.is_active && same(r.team_id, t.id)).map((r) => territoryRows.find((x) => same(x.id, r.territory_id))?.name))],
    in_use: members.length + routeRows.filter((r) => same(r.team_id, t.id) || same(r.backup_team_id, t.id)).length,
  };
};
const presentStaff = (s) => {
  const u = users.find((x) => same(x.id, s.user_id));
  return {
    ...s,
    department: deptRef(s.department_id),
    team: teamRef(s.team_id),
    supervisor: person(s.supervisor_id),
    user: u ? { id: u.id, name: u.name, email: u.email, status: u.status, roles: u.roles } : null,
    in_use: s.owns + (s.user_id ? 1 : 0),
  };
};
const presentRegion = (r) => ({ ...r, territories_count: territoryRows.filter((t) => same(t.sales_region_id, r.id)).length, in_use: territoryRows.filter((t) => same(t.sales_region_id, r.id)).length });
const presentTerritory = (t) => {
  const r = regionRows.find((x) => same(x.id, t.sales_region_id));
  return {
    ...t,
    region: r ? { id: r.id, name: r.name } : null,
    locations_count: locationRows.filter((l) => same(l.territory_id, t.id)).length,
    teams: [...new Set(routeRows.filter((x) => x.is_active && same(x.territory_id, t.id)).map((x) => teamRef(x.team_id)?.name))],
    in_use: locationRows.filter((l) => same(l.territory_id, t.id)).length + routeRows.filter((x) => same(x.territory_id, t.id)).length,
  };
};
const presentLocation = (l) => {
  const t = territoryRows.find((x) => same(x.id, l.territory_id));
  const r = regionRows.find((x) => same(x.id, t?.sales_region_id));
  const cust = customers.filter((c) => same(c.location_id, l.id)).length;
  const prs = prospects.filter((p) => same(p.location_id, l.id)).length;
  return { ...l, territory: t ? { id: t.id, name: t.name } : null, region: r ? { id: r.id, name: r.name } : null, in_use: cust + prs, customers_count: cust, prospects_count: prs };
};
const presentRoute = (r) => ({
  ...r,
  territory: (({ id, name } = {}) => (id ? { id, name } : null))(territoryRows.find((t) => same(t.id, r.territory_id))),
  team: teamRef(r.team_id),
  backup_team: teamRef(r.backup_team_id),
  rule_label: meta.routing_rules.find((x) => x.key === r.rule)?.label ?? r.rule,
});
const presentProduct = (p) => {
  const st = meta.product_statuses.find((s) => s.key === p.status);
  const pk = packageRows.filter((k) => same(k.product_id, p.id)).length;
  const clients = customers.filter((c) => c.product_ids.includes(Number(p.id))).length;
  return {
    ...p, owner: person(p.owner_staff_id), status_label: st?.name ?? p.status, status_tone: st?.tone ?? "secondary",
    deployment_label: meta.deployments.find((d) => d.key === p.deployment)?.label, can_be_sold: p.status === "active" && p.is_sellable,
    packages_count: pk, clients_count: clients, in_use: pk + clients,
  };
};
const presentPrice = (x) => {
  const k = packageRows.find((p) => same(p.id, x.package_id));
  return { ...x, package: k ? { id: k.id, name: k.name, product: productRows.find((p) => same(p.id, k.product_id))?.name } : null };
};
const presentPackage = (k) => {
  const p = productRows.find((x) => same(x.id, k.product_id));
  const pending = priceRows.find((x) => same(x.package_id, k.id) && x.status === "pending");
  return {
    ...k, product: p ? { id: p.id, code: p.code, name: p.name } : null, pending_price: pending ? presentPrice(pending) : null,
    in_use: policyRows.filter((x) => same(x.package_id, k.id)).length + k.subscriptions_count,
  };
};
const presentPriceList = (l) => ({ ...l, period: periodOf(l) });
const presentReason = (r) => {
  const c = categoryRows.find((x) => same(x.id, r.reason_category_id));
  return { ...r, category: c ? { id: c.id, key: c.key, name: c.name, tone: c.tone } : null };
};
const presentCategory = (c) => ({ ...c, reasons_count: reasonRows.filter((r) => same(r.reason_category_id, c.id)).length, in_use: reasonRows.filter((r) => same(r.reason_category_id, c.id)).length });
const presentField = (f) => ({
  ...f,
  entity_label: meta.custom_field_entities.find((e) => e.key === f.entity)?.label ?? f.entity,
  type_label: meta.custom_field_types.find((t) => t.key === f.type)?.label ?? f.type,
});
const presentPolicy = (p) => {
  const pr = priorityRow(p.priority_id);
  const seg = lookupLists.segments.rows.find((s) => same(s.id, p.segment_id));
  const cal = calendarRows.find((c) => same(c.id, p.business_calendar_id));
  const pkg = packageRows.find((k) => same(k.id, p.package_id));
  return {
    ...p,
    priority: pr ? { id: pr.id, value: pr.value, label: pr.label, tone: pr.meta?.tone } : null,
    segment: seg ? { id: seg.id, value: seg.value, label: seg.label } : null,
    package: pkg ? { id: pkg.id, name: pkg.name, product: productRows.find((x) => same(x.id, pkg.product_id))?.name } : null,
    is_base: !p.segment_id && !p.package_id && !p.is_override,
    calendar: cal ? { id: cal.id, name: cal.name } : null,
    escalate_to_label: p.escalate_to_label ?? roles.find((r) => r.key === p.escalate_to_value)?.label ?? p.escalate_to_value,
  };
};
const presentCalendar = (c) => ({
  ...c,
  weekly_hours: Math.round((c.days.reduce((s, d) => {
    if (!d.is_working || !d.opens_at || !d.closes_at) return s;
    const [oh, om] = d.opens_at.split(":").map(Number);
    const [ch, cm] = d.closes_at.split(":").map(Number);
    return s + Math.max(0, ch * 60 + cm - (oh * 60 + om));
  }, 0) / 60) * 10) / 10,
  in_use: policyRows.filter((p) => same(p.business_calendar_id, c.id)).length,
});
const levelWhen = (l) => {
  const basis = l.basis === "resolution" ? "Resolution" : "Response";
  const on = l.priority_id ? ` on ${priorityRow(l.priority_id)?.label}` : "";
  return (l.after_percent >= 100 ? `${basis} breached` : `${l.after_percent}% of ${basis.toLowerCase()} time used`) + on;
};

/* ------------------------------------------------------------------ */
/* Shared rules: reasons, in-use refusals                               */
/* ------------------------------------------------------------------ */

const needReason = (body) => (body?.reason ? null : invalid({ reason: ["Give a reason for the change; it goes into the change history."] }));
const inUse = (name, count) =>
  fail(`${name} is in use by ${count} record(s), so it cannot be deleted. Deactivate it instead: it leaves the pick-lists, and the records that use it keep it.`, { code: "CRM_004", details: { in_use: true, usage: { records: count } } }, 409);
const log = (area, setting, oldValue, newValue, body) => recordChange(area, setting, oldValue ?? "—", newValue, body?.reason || "—", body?.effective_date || today());
const describe = (o, fields) => fields.map(([k, label]) => `${label}: ${o[k] ?? "—"}`).join(" · ");

/**
 * The seven routes of one list: index, show, store, update, deactivate,
 * reactivate, destroy. `cfg` names the area, the noun, the rows, the
 * presenter and how a row reads in the change log.
 */
const crudRoutes = (path, cfg) => {
  const find = (id) => cfg.rows.find((r) => same(r.id, id));
  const name = (r) => cfg.name(r);
  const field = cfg.switchField ?? "is_active";
  const on = cfg.onValue ?? true;
  const off = cfg.offValue ?? false;
  return [
    ["get", `${BASE}/${path}`, ({ query }) => {
      const rows = (cfg.filter ? cfg.rows.filter((r) => cfg.filter(r, query)) : cfg.rows).map(cfg.present);
      return ok(cfg.paged ? (({ data, current_page, last_page, per_page, total }) => ({ data, links: {}, meta: { current_page, last_page, per_page, total } }))(paginate(rows, query, 25)) : rows);
    }],
    ["get", `${BASE}/${path}/:id`, ({ params }) => (find(params.id) ? ok(cfg.present(find(params.id))) : notFound())],
    ["post", `${BASE}/${path}`, ({ body }) => {
      const errors = cfg.validate?.(body, null);
      if (errors) return invalid(errors);
      const row = { id: nextId(cfg.rows), [field]: on, ...cfg.make(body) };
      cfg.rows.push(row);
      log(cfg.area, `${cfg.noun}: ${name(row)}`, "—", "Added", body);
      return created(cfg.present(row), `${name(row)} added`);
    }],
    ["put", `${BASE}/${path}/:id`, ({ params, body }) => {
      const row = find(params.id);
      if (!row) return notFound();
      const missing = needReason(body);
      if (missing) return missing;
      const errors = cfg.validate?.(body, row);
      if (errors) return invalid(errors);
      const before = cfg.summary ? cfg.summary(row) : name(row);
      Object.assign(row, cfg.apply ? cfg.apply(body, row) : body);
      delete row.reason;
      delete row.effective_date;
      log(cfg.area, `${cfg.noun}: ${name(row)}`, before, cfg.summary ? cfg.summary(row) : name(row), body);
      return ok(cfg.present(row), `${name(row)} saved`);
    }],
    ["post", `${BASE}/${path}/:id/deactivate`, ({ params, body }) => {
      const row = find(params.id);
      if (!row) return notFound();
      const missing = needReason(body);
      if (missing) return missing;
      const refused = cfg.beforeDeactivate?.(row);
      if (refused) return refused;
      row[field] = off;
      log(cfg.area, `${cfg.noun}: ${name(row)}`, "Active", "Inactive", body);
      return ok(cfg.present(row), `${name(row)} deactivated`);
    }],
    ["post", `${BASE}/${path}/:id/reactivate`, ({ params, body }) => {
      const row = find(params.id);
      if (!row) return notFound();
      const missing = needReason(body);
      if (missing) return missing;
      row[field] = on;
      log(cfg.area, `${cfg.noun}: ${name(row)}`, "Inactive", "Active", body);
      return ok(cfg.present(row), `${name(row)} reactivated`);
    }],
    ["delete", `${BASE}/${path}/:id`, ({ params, body }) => {
      const row = find(params.id);
      if (!row) return notFound();
      const used = cfg.present(row).in_use ?? 0;
      if (used > 0) return inUse(name(row), used);
      cfg.rows.splice(cfg.rows.indexOf(row), 1);
      log(cfg.area, `${cfg.noun}: ${name(row)}`, name(row), "Deleted", body);
      return ok(null, `${name(row)} deleted`);
    }],
  ];
};

const required = (body, row, keys) => {
  const errors = {};
  for (const [k, label] of keys) if (!row && (body[k] === undefined || body[k] === null || body[k] === "")) errors[k] = [`The ${label} field is required.`];
  return Object.keys(errors).length ? errors : null;
};

/* ------------------------------------------------------------------ */
/* Routes                                                               */
/* ------------------------------------------------------------------ */

const changesPage = (query) => {
  const areas = String(query.area ?? "").split(",").filter(Boolean);
  const rows = configLog
    .filter((c) => !areas.length || areas.includes(c.area))
    .filter((c) => matches(query.search, c.setting, c.reason, c.by))
    .sort((a, b) => (a.at < b.at ? 1 : -1))
    .map((c) => ({ id: c.id, area: c.area, area_label: meta.areas.find((a) => a.key === c.area)?.label ?? c.area, entity_type: null, entity_id: null, action: "updated", setting: c.setting, old: c.old, new: c.new, reason: c.reason, effective: c.effective, by: c.by, at: c.at }));
  const page = paginate(rows, query, 25);
  return { data: page.data, links: {}, meta: { current_page: page.current_page, last_page: page.last_page, per_page: page.per_page, total: page.total } };
};

const lookupRoutes = [
  ["get", `${BASE}/lookups`, () =>
    ok({ lists: Object.entries(lookupLists).map(([key, l]) => ({ key, name: l.name, singular: l.singular, help: l.help, used_in: l.used_in, fields: l.fields, per_product: !!l.per_product, count: l.rows.length, active: l.rows.filter((r) => r.is_active).length })) })],
  ["get", `${BASE}/lookups/:list`, ({ params, query }) => {
    const l = lookupLists[params.list];
    if (!l) return notFound("Lookup list was not found.");
    return ok([...l.rows].sort((a, b) => a.sort_order - b.sort_order).filter((r) => matches(query.search, r.label)));
  }],
  ["post", `${BASE}/lookups/:list/reorder`, ({ params, body }) => {
    const l = lookupLists[params.list];
    if (!l) return notFound();
    const before = [...l.rows].sort((a, b) => a.sort_order - b.sort_order).map((r) => r.label).join(", ");
    (body.ids ?? []).forEach((id, i) => {
      const r = l.rows.find((x) => same(x.id, id));
      if (r) r.sort_order = i + 1;
    });
    log("lookups", `${l.name} · order`, before, [...l.rows].sort((a, b) => a.sort_order - b.sort_order).map((r) => r.label).join(", "), body);
    return ok([...l.rows].sort((a, b) => a.sort_order - b.sort_order), "Order saved");
  }],
  ["post", `${BASE}/lookups/:list`, ({ params, body }) => {
    const l = lookupLists[params.list];
    if (!l) return notFound();
    const label = String(body.label ?? "").trim();
    if (!label) return invalid({ label: ["The label field is required."] });
    if (l.rows.some((r) => r.label.toLowerCase() === label.toLowerCase())) return invalid({ label: [`"${label}" is already on the list.`] });
    const row = { id: lookupId++, list: params.list, value: body.value || slug(label), label, sort_order: Math.max(0, ...l.rows.map((r) => r.sort_order)) + 1, is_active: true, is_default: false, meta: body.meta ?? {}, product: null, in_use: 0 };
    l.rows.push(row);
    log("lookups", `${l.name}: ${label}`, "—", "Added", body);
    return created(row, `"${label}" added`);
  }],
  ["put", `${BASE}/lookups/:list/:id`, ({ params, body }) => {
    const l = lookupLists[params.list];
    const row = l?.rows.find((r) => same(r.id, params.id));
    if (!row) return notFound();
    const missing = needReason(body);
    if (missing) return missing;
    const label = body.label !== undefined ? String(body.label).trim() : row.label;
    if (l.rows.some((r) => r !== row && r.label.toLowerCase() === label.toLowerCase())) return invalid({ label: [`"${label}" is already on the list.`] });
    const before = `${row.label}${Object.values(row.meta ?? {}).length ? ` (${Object.values(row.meta).join(", ")})` : ""}`;
    row.label = label;
    if (body.meta) row.meta = { ...row.meta, ...body.meta };
    log("lookups", `${l.name}: ${row.label}`, before, `${row.label}${Object.values(row.meta ?? {}).length ? ` (${Object.values(row.meta).join(", ")})` : ""}`, body);
    return ok(row, "Saved");
  }],
  ...["deactivate", "reactivate"].map((act) => ["post", `${BASE}/lookups/:list/:id/${act}`, ({ params, body }) => {
    const l = lookupLists[params.list];
    const row = l?.rows.find((r) => same(r.id, params.id));
    if (!row) return notFound();
    const missing = needReason(body);
    if (missing) return missing;
    row.is_active = act === "reactivate";
    log("lookups", `${l.name}: ${row.label}`, row.is_active ? "Inactive" : "Active", row.is_active ? "Active" : "Inactive", body);
    return ok(row, row.is_active ? "Reactivated" : "Deactivated");
  }]),
  ["delete", `${BASE}/lookups/:list/:id`, ({ params }) => {
    const l = lookupLists[params.list];
    const row = l?.rows.find((r) => same(r.id, params.id));
    if (!row) return notFound();
    if (row.in_use > 0) return inUse(`"${row.label}"`, row.in_use);
    l.rows.splice(l.rows.indexOf(row), 1);
    log("lookups", `${l.name}: ${row.label}`, "Active", "Deleted", { reason: "Unused value removed" });
    return ok(null, "Deleted");
  }],
];

const stageRoutes = [
  ["get", `${BASE}/stages`, () =>
    ok({
      entities: Object.entries(stageEntities).map(([key, e]) => ({ key, label: e.label, singular: e.singular, needs_lost: e.needs_lost, probability: e.probability })),
      kinds: meta.stage_kinds,
      lists: Object.fromEntries(Object.entries(stageLists).map(([entity, l]) => [entity, { entity, stages: [...l.stages].sort((a, b) => a.sort_order - b.sort_order), transitions: l.transitions }])),
    })],
  ["put", `${BASE}/stages/:entity`, ({ params, body }) => {
    const list = stageLists[params.entity];
    if (!list) return notFound();
    const missing = needReason(body);
    if (missing) return missing;
    const active = (body.stages ?? []).filter((s) => s.is_active !== false);
    const problems = [];
    if (!active.some((s) => s.kind === "open")) problems.push("At least one open stage is needed.");
    if (!active.some((s) => s.kind === "won")) problems.push("At least one won / done stage is needed; conversion and win-rate reports count it.");
    if (stageEntities[params.entity].needs_lost && !active.some((s) => s.kind === "lost")) problems.push("At least one lost stage is needed so drop-off reasons can be recorded.");
    if (problems.length) return invalid({ stages: problems });
    const before = list.stages.filter((s) => s.is_active).map((s) => s.name).join(" → ");
    body.stages.forEach((row, i) => {
      const stage = list.stages.find((s) => (row.id && same(s.id, row.id)) || s.key === row.key);
      const data = {
        name: row.name, kind: row.kind, sort_order: row.kind === "paused" ? 90 + i : row.kind === "lost" ? 99 : i + 1,
        probability: stageEntities[params.entity].probability ? (row.kind === "won" ? 100 : row.kind === "lost" ? 0 : row.probability ?? null) : null,
        max_days: ["open", "paused"].includes(row.kind) ? row.max_days ?? null : null,
        required_on_entry: row.required_on_entry ?? stage?.required_on_entry ?? [], is_active: row.is_active !== false,
      };
      if (stage) Object.assign(stage, data);
      else list.stages.push({ id: stageId++, entity: params.entity, key: row.key, default_name: null, is_system: false, in_use: 0, ...data });
    });
    if (body.transitions) list.transitions = body.transitions.map((t) => ({ id: `${t.from}>${t.to}`, from: t.from, to: t.to, required_fields: t.required_fields ?? [] }));
    log("stages", stageEntities[params.entity].label, before, list.stages.filter((s) => s.is_active).sort((a, b) => a.sort_order - b.sort_order).map((s) => s.name).join(" → "), body);
    return ok({ entity: params.entity, stages: [...list.stages].sort((a, b) => a.sort_order - b.sort_order), transitions: list.transitions }, `${stageEntities[params.entity].label} saved`);
  }],
  ["post", `${BASE}/stages/:entity/restore-defaults`, ({ params, body }) => {
    const list = stageLists[params.entity];
    if (!list) return notFound();
    const keep = list.stages.filter((s) => !s.is_system && s.in_use > 0).map((s) => ({ ...s, is_active: false }));
    list.stages = [...structuredClone(defaultStages[params.entity].stages), ...keep];
    list.transitions = structuredClone(defaultStages[params.entity].transitions);
    log("stages", stageEntities[params.entity].label, "Customised", "Restored to the defaults", body);
    return ok({ entity: params.entity, stages: list.stages, transitions: list.transitions });
  }],
  ["delete", `${BASE}/stages/:entity/:id`, ({ params }) => {
    const list = stageLists[params.entity];
    const stage = list?.stages.find((s) => same(s.id, params.id));
    if (!stage) return notFound();
    if (stage.in_use > 0) return inUse(stage.name, stage.in_use);
    list.stages.splice(list.stages.indexOf(stage), 1);
    list.transitions = list.transitions.filter((t) => t.from !== stage.key && t.to !== stage.key);
    log("stages", `${stageEntities[params.entity].singular}: ${stage.name}`, stage.name, "Deleted", {});
    return ok({ entity: params.entity, stages: list.stages, transitions: list.transitions }, `${stage.name} deleted`);
  }],
  ["get", `${BASE}/workflow-rules`, () => ok(workflowRules())],
  ["put", `${BASE}/workflow-rules`, ({ body }) => {
    const missing = needReason(body);
    if (missing) return missing;
    for (const [key, on] of Object.entries(body.conversion_rules ?? {})) {
      const rule = conversionRules.find((r) => r.key === key);
      if (rule && !rule.locked && rule.on !== on) {
        log("stages", `Rule: ${rule.label}`, rule.on ? "On" : "Off", on ? "On" : "Off", body);
        rule.on = on;
      }
    }
    for (const [key, value] of Object.entries(body.pipeline ?? {})) {
      if (JSON.stringify(pipelineRules[key]) !== JSON.stringify(value)) {
        log("stages", key.replaceAll("_", " "), String(pipelineRules[key]), String(value), body);
        pipelineRules[key] = structuredClone(value);
      }
    }
    return ok(workflowRules(), "Workflow rules saved");
  }],
];

const priceRoutes = [
  ["get", `${BASE}/price-changes`, ({ query }) => {
    const rows = priceRows.filter((x) => !query.status || x.status === query.status).map(presentPrice);
    const page = paginate(rows, query, 25);
    return ok({ data: page.data, links: {}, meta: { current_page: page.current_page, last_page: page.last_page, per_page: page.per_page, total: page.total } });
  }],
  ["get", `${BASE}/packages/:id/prices`, ({ params }) => ok(priceRows.filter((x) => same(x.package_id, params.id)).map(presentPrice))],
  ["post", `${BASE}/packages/:id/prices`, ({ params, body }) => {
    const k = packageRows.find((x) => same(x.id, params.id));
    if (!k) return notFound();
    const missing = needReason(body);
    if (missing) return missing;
    if (!(Number(body.price) > 0)) return invalid({ price: ["The new price must be greater than 0."] });
    if (priceRows.some((x) => same(x.package_id, k.id) && x.status === "pending")) return fail(`A price change for ${k.name} is already waiting for approval. Approve or reject it first.`, { code: "CRM_004" }, 409);
    const row = { id: nextId(priceRows), package_id: k.id, previous_price: k.price, price: Number(body.price), effective_from: body.effective_from || today(), effective_to: null, status: "pending", reason: body.reason, requested_by: { id: 1, name: "Jane Wanjiku" }, decided_by: null, decided_at: null, decision_note: null, created_at: now() };
    priceRows.unshift(row);
    log("products", `Price request · ${presentPackage(k).product?.name} · ${k.name}`, `KES ${k.price.toLocaleString()}`, `KES ${row.price.toLocaleString()} (awaiting Finance)`, body);
    return created(presentPrice(row), `New price for ${k.name} sent to Finance for approval. It applies only once approved.`);
  }],
  ...["approve", "reject"].map((act) => ["post", `${BASE}/price-changes/:id/${act}`, ({ params, body }) => {
    const x = priceRows.find((r) => same(r.id, params.id));
    if (!x) return notFound();
    if (x.status !== "pending") return fail(`This price change has already been ${x.status}.`, { code: "CRM_004" }, 409);
    if (same(x.requested_by?.id, 1)) return fail("You asked for this price change, so somebody else must approve or reject it.", { code: "CRM_005" }, 403);
    if (act === "reject" && !body.reason) return invalid({ reason: ["Say why the price is rejected."] });
    x.status = act === "approve" ? "approved" : "rejected";
    x.decided_by = { id: 1, name: "Jane Wanjiku" };
    x.decided_at = now();
    x.decision_note = body.reason ?? null;
    const k = packageRows.find((p) => same(p.id, x.package_id));
    if (act === "approve" && k && daysUntil(x.effective_from) <= 0) k.price = x.price;
    log("products", `Price ${k?.name}`, `KES ${Number(x.previous_price).toLocaleString()}`, `KES ${x.price.toLocaleString()} · ${x.status}`, { reason: body.reason || (act === "approve" ? "Approved" : "Rejected"), effective_date: x.effective_from });
    return ok(presentPrice(x), act === "approve" ? "Price approved." : "Price change rejected");
  }]),
  ...["approve", "reject"].map((act) => ["post", `${BASE}/price-lists/:id/${act}`, ({ params, body }) => {
    const l = priceListRows.find((r) => same(r.id, params.id));
    if (!l) return notFound();
    if (l.status !== "pending") return fail(`This price list has already been ${l.status}.`, { code: "CRM_004" }, 409);
    if (same(l.requested_by?.id, 1)) return fail("You asked for this price list, so somebody else must approve or reject it.", { code: "CRM_005" }, 403);
    l.status = act === "approve" ? "approved" : "rejected";
    l.decided_by = { id: 1, name: "Jane Wanjiku" };
    log("products", `Price list ${l.name}`, "Awaiting Finance", l.status === "approved" ? "Approved" : "Rejected", { reason: body.reason || "Maker-checker approval", effective_date: l.effective_from });
    return ok(presentPriceList(l), `${l.name} ${l.status}`);
  }]),
];

const calendarRoutes = [
  ["get", `${BASE}/business-calendars`, () => ok(calendarRows.map(presentCalendar))],
  ["put", `${BASE}/business-calendars/:id`, ({ params, body }) => {
    const c = calendarRows.find((x) => same(x.id, params.id));
    if (!c) return notFound();
    const missing = needReason(body);
    if (missing) return missing;
    const fmt = (d) => (d.is_working ? `${d.opens_at}–${d.closes_at}` : "Closed");
    const changed = [];
    for (const d of body.days ?? []) {
      const row = c.days.find((x) => x.weekday === d.weekday);
      if (!row) continue;
      if (d.is_working && (!d.opens_at || !d.closes_at || d.closes_at <= d.opens_at)) return invalid({ days: ["Closing time must be after opening time."] });
      const before = fmt(row);
      Object.assign(row, { is_working: d.is_working, opens_at: d.is_working ? d.opens_at : null, closes_at: d.is_working ? d.closes_at : null });
      if (before !== fmt(row)) changed.push([row.day, before, fmt(row)]);
    }
    if (changed.length) log("sla", `Business hours (${c.name})`, changed.map((x) => `${x[0]} ${x[1]}`).join("; "), changed.map((x) => `${x[0]} ${x[2]}`).join("; "), body);
    return ok(presentCalendar(c), `${c.name} saved`);
  }],
];

export default [
  ["get", `${BASE}/meta`, () => ok(meta)],
  ["get", `${BASE}/changes`, ({ query }) => ok(changesPage(query))],
  ["get", "tenant/users-choices", () => ok(users.filter((u) => u.status === "active").map((u) => ({ id: u.id, name: u.name, email: u.email, job_title: u.roles?.[0] ?? null })))],
  ["get", `${BASE}/staff-choices`, () => ok(staffRows.filter((s) => s.status === "active").map(({ id, staff_no, name, title, department_id, team_id }) => ({ id, staff_no, name, title, department_id, team_id })))],

  // Organisation
  ...crudRoutes("departments", {
    area: "organisation", noun: "Department", rows: deptRows, present: presentDepartment, name: (d) => d.name,
    summary: (d) => `${d.name} · HOD ${person(d.head_staff_id)?.name ?? "—"}`,
    validate: (b, row) => required(b, row, [["code", "code"], ["name", "name"]]) ?? (!row && deptRows.some((d) => d.code === String(b.code).toUpperCase()) ? { code: ["A department with this code already exists."] } : null),
    make: (b) => ({ code: String(b.code).toUpperCase(), name: b.name, description: b.description ?? null, head_staff_id: b.head_staff_id ?? null }),
    apply: (b, d) => ({ name: b.name ?? d.name, head_staff_id: "head_staff_id" in b ? b.head_staff_id : d.head_staff_id, description: b.description ?? d.description }),
    beforeDeactivate: (d) => {
      const n = staffRows.filter((s) => same(s.department_id, d.id) && s.status === "active").length;
      return n ? fail(`${d.name} still has ${n} active staff. Move them to another department first.`, { code: "CRM_004" }, 409) : null;
    },
  }),
  ...crudRoutes("teams", {
    area: "organisation", noun: "Team", rows: teamRows, present: presentTeam, name: (t) => t.name,
    summary: (t) => `${t.name} · ${deptRef(t.department_id)?.name} · ${person(t.lead_staff_id)?.name ?? "no lead"}`,
    filter: (t, q) => !q.department_id || same(t.department_id, q.department_id),
    validate: (b, row) => required(b, row, [["name", "name"], ["department_id", "department"]]),
    make: (b) => ({ name: b.name, department_id: b.department_id, lead_staff_id: b.lead_staff_id ?? null }),
    apply: (b, t) => {
      const next = { name: b.name ?? t.name, department_id: b.department_id ?? t.department_id, lead_staff_id: b.lead_staff_id ?? null };
      staffRows.filter((s) => same(s.team_id, t.id)).forEach((s) => (s.department_id = next.department_id));
      return next;
    },
    beforeDeactivate: (t) => {
      const routed = presentTeam(t).territories;
      return routed.length ? fail(`${t.name} still receives new prospects for ${routed.join(", ")}. Change the territory routing first.`, { code: "CRM_004" }, 409) : null;
    },
  }),
  ...crudRoutes("staff", {
    area: "organisation", noun: "Staff", rows: staffRows, present: presentStaff, name: (s) => s.name, paged: true,
    switchField: "status", onValue: "active", offValue: "inactive",
    summary: (s) => `${s.title} · ${deptRef(s.department_id)?.name ?? "—"} / ${teamRef(s.team_id)?.name ?? "—"} / reports to ${person(s.supervisor_id)?.name ?? "—"}`,
    filter: (s, q) => matches(q.search, s.name, s.staff_no) && (!q.department_id || same(s.department_id, q.department_id)) && (!q.status || s.status === q.status) && (!q.no_login || !s.user_id),
    validate: (b, row) => required(b, row, [["name", "name"], ["title", "role / title"], ["department_id", "department"]]) ?? (row && same(b.supervisor_id, row.id) ? { supervisor_id: ["A person cannot supervise themselves."] } : null),
    make: (b) => ({
      staff_no: `STF-${String(Math.max(...staffRows.map((s) => Number(String(s.staff_no).slice(4)) || 0)) + 1).padStart(3, "0")}`,
      name: b.name, title: b.title, email: b.email ?? null, phone: b.phone ?? null, department_id: b.department_id, team_id: b.team_id ?? null,
      supervisor_id: b.supervisor_id ?? null, user_id: b.user_id ?? null, status: "active", owns: 0,
    }),
    apply: (b, s) => ({ name: b.name ?? s.name, title: b.title ?? s.title, department_id: b.department_id ?? s.department_id, team_id: b.team_id ?? null, supervisor_id: b.supervisor_id ?? null, user_id: "user_id" in b ? b.user_id : s.user_id }),
    beforeDeactivate: (s) => {
      const u = users.find((x) => same(x.id, s.user_id));
      return u?.status === "active" ? fail(`${s.name} still has an active login. Suspend it under System administration → Users first, so access is closed at the same time.`, { code: "CRM_004" }, 409) : null;
    },
  }),

  // Territories
  ...crudRoutes("sales-regions", { area: "territories", noun: "Sales region", rows: regionRows, present: presentRegion, name: (r) => r.name, validate: (b, row) => required(b, row, [["name", "name"]]), make: (b) => ({ name: b.name }) }),
  ...crudRoutes("territories", {
    area: "territories", noun: "Territory", rows: territoryRows, present: presentTerritory, name: (t) => t.name,
    summary: (t) => `${t.name} · ${regionRows.find((r) => same(r.id, t.sales_region_id))?.name}`,
    validate: (b, row) => required(b, row, [["name", "name"], ["sales_region_id", "sales region"]]),
    make: (b) => ({ name: b.name, sales_region_id: b.sales_region_id }),
  }),
  ...crudRoutes("locations", {
    area: "territories", noun: "Location", rows: locationRows, present: presentLocation, name: (l) => `${l.town}, ${l.county}`,
    summary: (l) => `${l.town}, ${l.county} · ${territoryRows.find((t) => same(t.id, l.territory_id))?.name}`,
    validate: (b, row) => required(b, row, [["country", "country"], ["county", "county"], ["town", "town"], ["territory_id", "territory"]])
      ?? (locationRows.some((l) => l !== row && l.town.toLowerCase() === String(b.town ?? row?.town).toLowerCase() && l.county.toLowerCase() === String(b.county ?? row?.county).toLowerCase()) ? { town: ["This town is already on the list."] } : null),
    make: (b) => ({ country: b.country, county: b.county, town: b.town, territory_id: b.territory_id }),
  }),
  ...crudRoutes("territory-routes", {
    area: "territories", noun: "Territory routing", rows: routeRows, present: presentRoute,
    name: (r) => `${territoryRows.find((t) => same(t.id, r.territory_id))?.name} → team`,
    summary: (r) => `${teamRef(r.team_id)?.name} (backup ${teamRef(r.backup_team_id)?.name ?? "—"}) · ${presentRoute(r).rule_label}${r.segments.length ? ` · ${r.segments.join("/")}` : ""}`,
    validate: (b, row) => required(b, row, [["territory_id", "territory"], ["team_id", "team"]]),
    make: (b) => ({ territory_id: b.territory_id, team_id: b.team_id, backup_team_id: b.backup_team_id ?? null, rule: b.rule ?? "round_robin", segments: b.segments ?? [], sort_order: routeRows.length + 1 }),
  }),

  // Products
  ...crudRoutes("products", {
    area: "products", noun: "Product", rows: productRows, present: presentProduct, name: (p) => p.name,
    switchField: "status", onValue: "active", offValue: "retired",
    summary: (p) => `${p.name} · ${person(p.owner_staff_id)?.name ?? "—"} · ${p.status}`,
    validate: (b, row) => required(b, row, [["code", "code"], ["name", "name"], ["owner_staff_id", "owner"]]) ?? (!row && productRows.some((p) => p.code === String(b.code).toUpperCase()) ? { code: ["A product with this code already exists."] } : null),
    make: (b) => ({ code: String(b.code).toUpperCase(), name: b.name, short_name: b.short_name ?? null, owner_staff_id: b.owner_staff_id, status: "in_development", deployment: b.deployment ?? "platform", is_sellable: b.is_sellable ?? true, description: null, domain: null, tenant_prefix: null, expected_release: null }),
  }),
  ...crudRoutes("packages", {
    area: "products", noun: "Package", rows: packageRows, present: presentPackage, name: (k) => k.name,
    summary: (k) => `${k.billing_period} · ${k.included_users} users · ${k.features.length} features`,
    filter: (k, q) => !q.product_id || same(k.product_id, q.product_id),
    validate: (b, row) => required(b, row, [["product_id", "product"], ["name", "name"], ["price", "price"]]),
    make: (b) => ({ product_id: b.product_id, name: b.name, price: Number(b.price), currency: "KES", billing_period: b.billing_period ?? "Monthly", unit_label: b.unit_label ?? null, included_users: b.included_users ?? 0, features: b.features ?? [], is_sellable: b.is_sellable ?? true, effective_from: null, effective_to: null, subscriptions_count: 0 }),
    apply: (b, k) => ({ name: b.name ?? k.name, billing_period: b.billing_period ?? k.billing_period, included_users: b.included_users ?? k.included_users, features: b.features ?? k.features, unit_label: b.unit_label ?? k.unit_label }),
  }),
  ...crudRoutes("price-lists", {
    area: "products", noun: "Price list", rows: priceListRows, present: presentPriceList, name: (l) => l.name,
    validate: (b, row) => required(b, row, [["name", "name"], ["effective_from", "effective from"]]),
    make: (b) => ({ name: b.name, effective_from: b.effective_from, effective_to: b.effective_to || null, note: b.note ?? null, status: "pending", requested_by: { id: 1, name: "Jane Wanjiku" }, decided_by: null, decided_at: null }),
  }),
  ...priceRoutes,

  // Stages and qualification
  ...stageRoutes,
  ...crudRoutes("qualification-items", {
    area: "stages", noun: "Qualification item", rows: qualificationRows, present: (q) => q, name: (q) => q.label,
    summary: (q) => `${q.label} · ${q.is_required ? "Mandatory" : "Optional"}`,
    filter: (q, query) => !query.entity || q.entity === query.entity,
    validate: (b, row) => required(b, row, [["entity", "entity"], ["label", "label"]]),
    make: (b) => ({ entity: b.entity, key: b.key || slug(b.label), label: b.label, is_required: !!b.is_required, sort_order: b.sort_order ?? qualificationRows.filter((q) => q.entity === b.entity).length + 1, in_use: 0 }),
  }),

  // Reasons
  ...crudRoutes("reason-categories", {
    area: "reason-codes", noun: "Category", rows: categoryRows, present: presentCategory, name: (c) => c.name,
    validate: (b, row) => required(b, row, [["name", "name"]]),
    make: (b) => ({ key: b.key || slug(b.name), name: b.name, tone: b.tone ?? "secondary", sort_order: categoryRows.length + 1 }),
  }),
  ...crudRoutes("reasons", {
    area: "reason-codes", noun: "Reason", rows: reasonRows, present: presentReason, name: (r) => `${r.code} ${r.name}`,
    summary: (r) => `${r.name} · ${categoryRows.find((c) => same(c.id, r.reason_category_id))?.name} · ${r.applies_to.join(", ")}`,
    filter: (r, q) => !q.point || r.applies_to.includes(q.point),
    validate: (b, row) => required(b, row, [["name", "name"], ["reason_category_id", "category"]]) ?? (b.applies_to && !b.applies_to.length ? { applies_to: ["Choose at least one point the reason applies to."] } : null),
    make: (b) => {
      const cat = categoryRows.find((c) => same(c.id, b.reason_category_id));
      const prefix = reasonRows.find((r) => same(r.reason_category_id, cat?.id))?.code.split("-")[0] ?? (cat?.key ?? "oth").slice(0, 3).toUpperCase();
      return {
        code: b.code || `${prefix}-${String(reasonRows.filter((r) => r.code.startsWith(prefix)).length + 1).padStart(2, "0")}`, name: b.name, reason_category_id: b.reason_category_id,
        applies_to: b.applies_to ?? [], needs_competitor: !!b.needs_competitor, recyclable: b.recyclable ?? true, default_revisit_days: b.default_revisit_days ?? null,
        preventable: !!b.preventable, note_required: !!b.note_required, sort_order: reasonRows.length + 1, in_use: 0,
      };
    },
  }),

  // Lookups
  ...lookupRoutes,

  // Custom fields
  ...crudRoutes("custom-fields", {
    area: "custom-fields", noun: "Custom field", rows: fieldRows, present: presentField, name: (f) => `${f.entity} · ${f.label}`,
    summary: (f) => `${f.type}${f.is_required ? ", required" : ""}${f.show_on_create ? ", on create" : ""}${f.is_sensitive ? ", sensitive" : ""} · ${f.visible_to_roles.join("/")}`,
    filter: (f, q) => !q.entity || f.entity === q.entity,
    validate: (b, row) => required(b, row, [["entity", "entity"], ["key", "key"], ["label", "label"], ["type", "type"]])
      ?? (!row && fieldRows.some((f) => f.entity === b.entity && f.key === b.key) ? { key: ["This record already has a field with that key."] } : null)
      ?? (!row && fieldRows.length >= meta.custom_field_limit ? { label: [`Your plan allows ${meta.custom_field_limit} custom fields.`] } : null),
    make: (b) => ({ entity: b.entity, key: b.key, label: b.label, type: b.type, options: b.options ?? [], is_required: !!b.is_required, show_on_create: b.show_on_create ?? true, visible_to_roles: b.visible_to_roles ?? ["*"], is_sensitive: !!b.is_sensitive, sort_order: fieldRows.length + 1, in_use: 0 }),
  }),

  // SLA
  ...crudRoutes("sla-policies", {
    area: "sla", noun: "SLA", rows: policyRows, present: presentPolicy, name: (p) => `${priorityRow(p.priority_id)?.label} · ${p.applies_to}`,
    summary: (p) => `response ${p.response_minutes} min, resolution ${p.resolution_minutes} min`,
    validate: (b, row) => required(b, row, [["priority_id", "priority"], ["response_minutes", "response"], ["resolution_minutes", "resolution"], ["business_calendar_id", "calendar"]]),
    make: (b) => {
      const seg = lookupLists.segments.rows.find((s) => same(s.id, b.segment_id));
      const pkg = packageRows.find((k) => same(k.id, b.package_id));
      return {
        name: null, priority_id: b.priority_id, segment_id: b.segment_id ?? null, package_id: b.package_id ?? null,
        applies_to: seg ? `Segment: ${seg.label}` : pkg ? `Package: ${productRows.find((p) => same(p.id, pkg.product_id))?.name} · ${pkg.name}` : "All customers",
        response_minutes: b.response_minutes, resolution_minutes: b.resolution_minutes, business_calendar_id: b.business_calendar_id,
        escalate_after_minutes: b.escalate_after_minutes ?? null, escalate_to_type: b.escalate_to_type ?? null, escalate_to_value: b.escalate_to_value ?? null, is_override: !!(seg || pkg), covered_count: 0,
      };
    },
    apply: (b) => {
      const out = { ...b };
      if ("escalate_to_value" in b) out.escalate_to_label = null;
      return out;
    },
  }),
  ...crudRoutes("escalation-levels", {
    area: "sla", noun: "Escalation level", rows: levelRows, name: (l) => String(l.level),
    present: (l) => ({ ...l, escalate_to_label: l.escalate_to_label ?? roles.find((r) => r.key === l.escalate_to_value)?.label ?? l.escalate_to_value, channels_label: (l.channels ?? []).map((c) => meta.channels.find((x) => x.key === c)?.label ?? c).join(", "), in_use: 0 }),
    summary: (l) => `${l.when} → ${l.escalate_to_label ?? l.escalate_to_value} (${(l.channels ?? []).join(", ")})`,
    validate: (b, row) => required(b, row, [["basis", "basis"], ["after_percent", "after"], ["escalate_to_value", "recipient"]]),
    make: (b) => {
      const row = { level: levelRows.length + 1, basis: b.basis, after_percent: b.after_percent, after_minutes: b.after_minutes ?? 0, priority_id: b.priority_id ?? null, escalate_to_type: b.escalate_to_type ?? "role", escalate_to_value: b.escalate_to_value, channels: b.channels ?? ["in_app"] };
      return { ...row, when: levelWhen(row), escalate_to_label: null };
    },
    apply: (b, l) => {
      const next = { ...l, ...b, escalate_to_label: null };
      return { ...b, when: levelWhen(next), escalate_to_label: null };
    },
  }),
  ...calendarRoutes,
  ...crudRoutes("holidays", {
    area: "sla", noun: "Holiday", rows: holidayRows, present: (h) => ({ ...h, in_use: 0 }), name: (h) => h.name,
    validate: (b, row) => required(b, row, [["date", "date"], ["name", "name"]]) ?? (holidayRows.some((h) => h !== row && h.date === b.date) ? { date: ["That date is already a holiday."] } : null),
    make: (b) => ({ date: b.date, name: b.name }),
  }),

  // Numbering
  ["get", `${BASE}/number-formats`, () => ok({ formats: formatRows.map(presentFormat), tokens: ["{prefix}", "{year}", "{month}", "{seq}"] })],
  ["put", `${BASE}/number-formats/:key`, ({ params, body }) => {
    const f = formatRows.find((x) => x.key === params.key);
    if (!f) return notFound();
    const missing = needReason(body);
    if (missing) return missing;
    if (body.next_number !== undefined && Number(body.next_number) < f.next_number) return fail(`The next number cannot go below ${f.next_number}: numbers are never reused.`, { code: "CRM_004" }, 409);
    const before = `${renderNumber(f)} (${f.format}, pad ${f.padding}${f.reset_yearly ? ", resets yearly" : ""})`;
    Object.assign(f, {
      prefix: body.prefix ?? f.prefix, format: body.format ?? f.format, padding: body.padding ?? f.padding,
      reset_yearly: body.reset_yearly ?? f.reset_yearly, next_number: body.next_number !== undefined ? Number(body.next_number) : f.next_number,
    });
    log("numbering", `${f.label} numbering`, before, `${renderNumber(f)} (${f.format}, pad ${f.padding}${f.reset_yearly ? ", resets yearly" : ""})`, body);
    return ok(presentFormat(f), `${f.label} numbering saved`);
  }],
];
